import { parseHex } from '../../core/color';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { DEG, clamp01, TAU } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { IncrementalHash } from '../../core/spatialHash';
import { SHAPE_KINDS, traceShape, type ShapeKind } from '../../core/shapes';
import type { RenderTarget, TechniqueContext } from '../types';
import type { CirclePackingValues } from './params';

/**
 * Circle packing by largest-fitting-radius.
 *
 * Rather than growing every circle simultaneously until it collides — which needs many
 * convergence passes and tends to leave a visible "growth ring" texture — each
 * candidate is placed once at the largest radius that still fits: the distance to its
 * nearest neighbour, to the text, and to the canvas edge, capped by the radius range.
 * One pass, no convergence, and the classic dense packing look.
 *
 * The neighbour query is the whole cost. An incremental uniform grid keeps it near
 * constant time; without one, 20 000 candidates would be 200 million distance tests.
 */
export interface CirclePackingState {
  x: Float32Array;
  y: Float32Array;
  r: Float32Array;
  /** Per-shape random values: rotation jitter, style choice, colour jitter. */
  jitter: Float32Array;
  /** 1 when the shape sits inside the glyphs. */
  inside: Uint8Array;
  placed: number;

  /** Candidate points still to try, as [x, y, ...]. */
  candidates: Float32Array;
  cursor: number;

  hash: IncrementalHash;
  palette: ResolvedPalette;
  insideColor: string;
  radiusCurve: Float32Array;
  capacity: number;
}

function values(ctx: TechniqueContext): CirclePackingValues {
  return ctx.values as unknown as CirclePackingValues;
}

/** Builds the candidate point list according to the placement strategy. */
function buildCandidates(ctx: TechniqueContext, v: CirclePackingValues): Float32Array {
  const { width, height, rng, field } = ctx;
  const n = v.candidates;
  const out = new Float32Array(n * 2);

  if (v.placement === 'poisson') {
    // A blue-noise ordering makes the *first* candidates spread out evenly, so the
    // large circles land apart from each other instead of clustering wherever the
    // random stream happened to start.
    const radius = Math.sqrt((width * height * 0.7) / Math.max(1, n));
    const cell = radius / Math.SQRT2;
    const cols = Math.ceil(width / cell) + 1;
    const rows = Math.ceil(height / cell) + 1;
    const grid = new Int32Array(cols * rows).fill(-1);
    const px: number[] = [];
    const py: number[] = [];
    const active: number[] = [];

    const fits = (x: number, y: number): boolean => {
      if (x < 0 || y < 0 || x >= width || y >= height) return false;
      const gx = (x / cell) | 0;
      const gy = (y / cell) | 0;
      for (let yy = Math.max(0, gy - 2); yy <= Math.min(rows - 1, gy + 2); yy++) {
        for (let xx = Math.max(0, gx - 2); xx <= Math.min(cols - 1, gx + 2); xx++) {
          const id = grid[yy * cols + xx];
          if (id < 0) continue;
          const dx = px[id] - x;
          const dy = py[id] - y;
          if (dx * dx + dy * dy < radius * radius) return false;
        }
      }
      return true;
    };
    const add = (x: number, y: number): void => {
      const id = px.length;
      px.push(x);
      py.push(y);
      grid[((y / cell) | 0) * cols + ((x / cell) | 0)] = id;
      active.push(id);
    };

    add(rng.next() * width, rng.next() * height);
    while (active.length && px.length < n) {
      const pick = rng.int(0, active.length - 1);
      const id = active[pick];
      let placed = false;
      for (let i = 0; i < 20; i++) {
        const a = rng.next() * TAU;
        const d = radius * (1 + rng.next());
        const x = px[id] + Math.cos(a) * d;
        const y = py[id] + Math.sin(a) * d;
        if (!fits(x, y)) continue;
        add(x, y);
        placed = true;
        break;
      }
      if (!placed) {
        active[pick] = active[active.length - 1];
        active.pop();
      }
    }
    const count = Math.min(n, px.length);
    for (let i = 0; i < count; i++) {
      out[i * 2] = px[i];
      out[i * 2 + 1] = py[i];
    }
    // Fall back to uniform points if the disc radius produced fewer than requested.
    for (let i = count; i < n; i++) {
      out[i * 2] = rng.next() * width;
      out[i * 2 + 1] = rng.next() * height;
    }
    return out;
  }

  if (v.placement === 'nearText' || v.placement === 'farText') {
    const reach = Math.max(1, field.maxOutside);
    const near = v.placement === 'nearText';
    let written = 0;
    let attempts = 0;
    while (written < n && attempts < n * 40) {
      attempts++;
      const x = rng.next() * width;
      const y = rng.next() * height;
      const t = clamp01(Math.abs(field.distance(x, y)) / reach);
      // Rejection sampling against a density that falls off with (or towards) distance.
      const accept = near ? (1 - t) ** 2 : t ** 1.5;
      if (rng.next() > accept) continue;
      out[written * 2] = x;
      out[written * 2 + 1] = y;
      written++;
    }
    for (let i = written; i < n; i++) {
      out[i * 2] = rng.next() * width;
      out[i * 2 + 1] = rng.next() * height;
    }
    return out;
  }

  for (let i = 0; i < n; i++) {
    out[i * 2] = rng.next() * width;
    out[i * 2 + 1] = rng.next() * height;
  }
  return out;
}

export function init(ctx: TechniqueContext): CirclePackingState {
  const v = values(ctx);
  const capacity = v.candidates;
  const maxRadius = Math.max(v.radius[0], v.radius[1]);

  return {
    x: new Float32Array(capacity),
    y: new Float32Array(capacity),
    r: new Float32Array(capacity),
    jitter: new Float32Array(capacity * 3),
    inside: new Uint8Array(capacity),
    placed: 0,
    candidates: buildCandidates(ctx, v),
    cursor: 0,
    // Cell size at the maximum radius keeps the ±2-cell search sufficient for any
    // pair of shapes that could possibly overlap.
    hash: new IncrementalHash(ctx.width, ctx.height, Math.max(4, maxRadius)),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    insideColor: v.colorInside,
    radiusCurve: bakeCurve(v.radiusByDistance as Curve),
    capacity,
  };
}

export function step(state: CirclePackingState, _dt: number, ctx: TechniqueContext): CirclePackingState {
  const v = values(ctx);
  const { field, width, height } = ctx;
  const minR = Math.min(v.radius[0], v.radius[1]);
  const maxR = Math.max(v.radius[0], v.radius[1]);
  const budget = Math.max(20, Math.round(v.perStep * clamp01(ctx.quality)));
  const end = Math.min(state.candidates.length / 2, state.cursor + budget);

  for (; state.cursor < end; state.cursor++) {
    const cx = state.candidates[state.cursor * 2];
    const cy = state.candidates[state.cursor * 2 + 1];

    const signed = field.distance(cx, cy);
    const isInside = signed < 0;
    if (v.mode === 'outside' && isInside) continue;
    if (v.mode === 'inside' && !isInside) continue;

    // Room available before touching the text, the canvas edge, or a neighbour.
    const roomToText = Math.abs(signed) - v.textGap;
    if (roomToText < minR) continue;

    const roomToEdge = Math.min(cx, cy, width - cx, height - cy);
    if (roomToEdge < minR) continue;

    // The radius curve maps distance-from-text onto a fraction of the allowed range.
    const t = clamp01(Math.abs(signed) / Math.max(1, v.distanceReach));
    const allowed = minR + (maxR - minR) * clamp01(sampleBaked(state.radiusCurve, t));

    let radius = Math.min(allowed, roomToText, roomToEdge);
    if (radius < minR) continue;

    // Shrink to clear every neighbour; bail out as soon as one leaves no room.
    let blocked = false;
    state.hash.query(cx, cy, radius + maxR + v.gap, (id) => {
      const dx = state.x[id] - cx;
      const dy = state.y[id] - cy;
      const available = Math.hypot(dx, dy) - state.r[id] - v.gap;
      if (available < radius) radius = available;
      if (radius < minR) {
        blocked = true;
        return false;
      }
      return true;
    });
    if (blocked || radius < minR) continue;

    const id = state.placed++;
    state.x[id] = cx;
    state.y[id] = cy;
    state.r[id] = radius;
    state.inside[id] = isInside ? 1 : 0;
    state.jitter[id * 3] = ctx.rng.next();
    state.jitter[id * 3 + 1] = ctx.rng.next();
    state.jitter[id * 3 + 2] = ctx.rng.next();
    state.hash.insert(cx, cy, id);
  }

  return state;
}

export function isComplete(state: CirclePackingState): boolean {
  return state.cursor >= state.candidates.length / 2;
}

export function draw(state: CirclePackingState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const minR = Math.min(v.radius[0], v.radius[1]);
  const maxR = Math.max(v.radius[0], v.radius[1]);
  const reach = Math.max(1, ctx.field.maxOutside);

  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  // Built once rather than per shape: parsing a hex string inside the draw loop shows
  // up immediately at a few thousand elements.
  const insideRgb = parseHex(state.insideColor);
  const insideColor = `rgba(${Math.round(insideRgb.r * 255)},${Math.round(insideRgb.g * 255)},${Math.round(insideRgb.b * 255)},${v.opacity})`;

  for (let i = 0; i < state.placed; i++) {
    const x = state.x[i];
    const y = state.y[i];
    const r = state.r[i];
    const j0 = state.jitter[i * 3];
    const j1 = state.jitter[i * 3 + 1];
    const j2 = state.jitter[i * 3 + 2];

    // ── Colour ──────────────────────────────────────────────────────────────
    let color: string;
    if (v.mode === 'both' && state.inside[i]) {
      color = insideColor;
    } else if (v.colorMode === 'solid') {
      color = state.palette.stop(0, v.opacity);
    } else {
      let t: number;
      switch (v.colorMode) {
        case 'distance':
          t = clamp01(Math.abs(ctx.field.distance(x, y)) / reach);
          break;
        case 'size':
          t = clamp01((r - minR) / Math.max(1e-6, maxR - minR));
          break;
        case 'order':
          t = i / Math.max(1, state.placed - 1);
          break;
        default:
          t = j2;
      }
      color = state.palette.at(t, v.opacity);
    }

    // ── Shape ───────────────────────────────────────────────────────────────
    const kind: ShapeKind =
      v.shape === 'mixed' ? SHAPE_KINDS[Math.floor(j0 * SHAPE_KINDS.length) % SHAPE_KINDS.length] : v.shape;
    const rotation = v.rotationJitter > 0 ? (j1 * 2 - 1) * v.rotationJitter * DEG : 0;

    const style =
      v.style === 'mixed' ? (j1 < v.fillRatio ? 'fill' : j0 < 0.22 ? 'rings' : 'stroke') : v.style;

    if (style === 'rings') {
      c.strokeStyle = color;
      const count = Math.max(2, v.rings);
      for (let k = 0; k < count; k++) {
        const rr = (r * (count - k)) / count;
        if (rr < 0.4) continue;
        const path = new Path2D();
        traceShape(path, kind, x, y, rr, rotation, v.starPoints);
        c.stroke(path);
      }
      continue;
    }

    const path = new Path2D();
    traceShape(path, kind, x, y, r, rotation, v.starPoints);
    if (style === 'fill') {
      c.fillStyle = color;
      c.fill(path);
    } else {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: CirclePackingState): number {
  return state.placed;
}

/**
 * Vector output.
 *
 * Circles become `<circle>` and the polygonal shapes become `<polygon>`, so the result
 * stays editable in a drawing program rather than arriving as one flattened path.
 */
export function toSVG(state: CirclePackingState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const minR = Math.min(v.radius[0], v.radius[1]);
  const maxR = Math.max(v.radius[0], v.radius[1]);
  const reach = Math.max(1, ctx.field.maxOutside);
  const insideRgb = parseHex(state.insideColor);
  const insideColor = `rgb(${Math.round(insideRgb.r * 255)},${Math.round(insideRgb.g * 255)},${Math.round(insideRgb.b * 255)})`;
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');

  const parts: string[] = [`<g fill-opacity="${v.opacity}" stroke-opacity="${v.opacity}" stroke-width="${v.lineWidth}">`];

  for (let i = 0; i < state.placed; i++) {
    const x = state.x[i];
    const y = state.y[i];
    const r = state.r[i];
    const j0 = state.jitter[i * 3];
    const j1 = state.jitter[i * 3 + 1];
    const j2 = state.jitter[i * 3 + 2];

    let color: string;
    if (v.mode === 'both' && state.inside[i]) color = insideColor;
    else if (v.colorMode === 'solid') color = state.palette.stop(0);
    else {
      let t: number;
      switch (v.colorMode) {
        case 'distance':
          t = clamp01(Math.abs(ctx.field.distance(x, y)) / reach);
          break;
        case 'size':
          t = clamp01((r - minR) / Math.max(1e-6, maxR - minR));
          break;
        case 'order':
          t = i / Math.max(1, state.placed - 1);
          break;
        default:
          t = j2;
      }
      color = state.palette.at(t);
    }

    const kind = v.shape === 'mixed' ? SHAPE_KINDS[Math.floor(j0 * SHAPE_KINDS.length) % SHAPE_KINDS.length] : v.shape;
    const rotation = v.rotationJitter > 0 ? (j1 * 2 - 1) * v.rotationJitter * DEG : 0;
    const style = v.style === 'mixed' ? (j1 < v.fillRatio ? 'fill' : j0 < 0.22 ? 'rings' : 'stroke') : v.style;

    const radii = style === 'rings' ? Array.from({ length: Math.max(2, v.rings) }, (_, k) => (r * (Math.max(2, v.rings) - k)) / Math.max(2, v.rings)) : [r];
    const paint = style === 'fill' ? `fill="${color}" stroke="none"` : `fill="none" stroke="${color}"`;

    for (const rr of radii) {
      if (rr < 0.3) continue;
      if (kind === 'circle') {
        parts.push(`<circle cx="${f(x)}" cy="${f(y)}" r="${f(rr)}" ${paint}/>`);
      } else {
        parts.push(`<polygon points="${shapePoints(kind, x, y, rr, rotation, v.starPoints, f)}" ${paint}/>`);
      }
    }
  }

  parts.push('</g>');
  return parts.join('\n');
}

/** Vertex list for the polygonal shapes, matching what `traceShape` draws. */
function shapePoints(
  kind: ShapeKind,
  x: number,
  y: number,
  r: number,
  rotation: number,
  starPoints: number,
  f: (n: number) => string,
): string {
  const out: string[] = [];
  if (kind === 'star') {
    const points = Math.max(3, Math.round(starPoints));
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 === 0 ? r : r * 0.45;
      const a = rotation - Math.PI / 2 + (i * Math.PI) / points;
      out.push(`${f(x + Math.cos(a) * radius)},${f(y + Math.sin(a) * radius)}`);
    }
    return out.join(' ');
  }
  const sides = kind === 'square' ? 4 : kind === 'triangle' ? 3 : 6;
  const offset = kind === 'square' ? Math.PI / 4 : -Math.PI / 2;
  for (let i = 0; i < sides; i++) {
    const a = rotation + offset + (i * TAU) / sides;
    out.push(`${f(x + Math.cos(a) * r)},${f(y + Math.sin(a) * r)}`);
  }
  return out.join(' ');
}
